import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

const INVITE_TOKEN_KEYS = [
  "token",
  "invite_token",
  "inviteToken",
  "t",
  "access_token",
];

const ORG_ID_KEYS = ["org_id", "organization_id", "orgId"];

function readParamFromSearchAndHash(keys) {
  if (typeof window === "undefined") return "";

  const sources = [
    window.location.search || "",
    window.location.hash?.includes("?")
      ? window.location.hash.slice(window.location.hash.indexOf("?"))
      : "",
    window.location.hash?.startsWith("#")
      ? `?${window.location.hash.slice(1)}`
      : "",
  ];

  for (const source of sources) {
    if (!source) continue;

    try {
      const params = new URLSearchParams(source.startsWith("?") ? source : `?${source}`);
      for (const key of keys) {
        const value = params.get(key);
        if (value && value.trim()) return value.trim();
      }
    } catch {
      // keep checking other sources
    }
  }

  return "";
}

function getInviteParams() {
  return {
    inviteToken: readParamFromSearchAndHash(INVITE_TOKEN_KEYS),
    orgId: readParamFromSearchAndHash(ORG_ID_KEYS),
  };
}

function getStorageItem(key) {
  if (typeof window === "undefined") return null;

  try {
    return localStorage.getItem(key) || sessionStorage.getItem(key) || null;
  } catch {
    return null;
  }
}

function setStorageItem(key, value) {
  if (typeof window === "undefined") return;

  try {
    if (value == null || value === "") return;
    localStorage.setItem(key, String(value));
    sessionStorage.setItem(key, String(value));
  } catch {
    // no-op
  }
}

function readSupabaseAccessToken() {
  if (typeof window === "undefined") return "";

  const stores = [window.localStorage, window.sessionStorage].filter(Boolean);

  for (const store of stores) {
    try {
      for (let i = 0; i < store.length; i += 1) {
        const key = store.key(i);
        if (!key || !key.startsWith("sb-") || !key.endsWith("-auth-token")) continue;

        const raw = store.getItem(key);
        if (!raw) continue;

        const parsed = JSON.parse(raw);
        const accessToken = parsed?.access_token || parsed?.currentSession?.access_token || "";
        if (accessToken) return accessToken;
      }
    } catch {
      // try next storage
    }
  }

  return "";
}

function removeStorageItem(key) {
  if (typeof window === "undefined") return;

  try {
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
  } catch {
    // no-op
  }
}

function clearLegacyTrackerTokens() {
  [
    "runtimeInviteToken",
    "trackerRuntimeToken",
    "tracker_runtime_token",
    "tracker_access_token",
    "geocercas-tracker-auth",
  ].forEach(removeStorageItem);
}

function buildTrackerGpsUrl(session) {
  const params = new URLSearchParams();
  params.set("tracker_runtime_token", session.runtimeToken);
  params.set("tracker_user_id", session.trackerUserId);
  params.set("org_id", session.orgId);
  return `/tracker-gps?${params.toString()}`;
}

function requestCurrentPositionOnce() {
  return new Promise((resolve, reject) => {
    if (!navigator?.geolocation) {
      reject(new Error("Geolocation API not available"));
      return;
    }

    navigator.geolocation.getCurrentPosition(resolve, reject, {
      enableHighAccuracy: true,
      maximumAge: 0,
      timeout: 60000,
    });
  });
}

function getGeoErrorMessage(error, t) {
  const code = error?.code;
  const raw = String(error?.message || "").toLowerCase();

  if (code === 1 || raw.includes("permission")) {
    return t("tracker.invite.errors.blockedSite");
  }

  if (code === 2) {
    return t("tracker.invite.errors.locationUnavailable");
  }

  if (code === 3 || raw.includes("timeout")) {
    return t("tracker.invite.errors.locationTimeout");
  }

  return t("tracker.invite.errors.permissionFailed");
}

async function getPermissionState() {
  try {
    if (!navigator?.permissions?.query) return "unknown";
    const result = await navigator.permissions.query({ name: "geolocation" });
    return result?.state || "unknown";
  } catch {
    return "unknown";
  }
}

async function ensureGeolocationPermissionByPrompt(t) {
  const state = await getPermissionState();

  if (state === "granted") {
    return { ok: true, permissionState: "granted" };
  }

  if (state === "denied") {
    return {
      ok: false,
      permissionState: "denied",
      message: t("tracker.invite.errors.blockedChrome"),
    };
  }

  try {
    await requestCurrentPositionOnce();
    return { ok: true, permissionState: "prompt_or_unknown" };
  } catch (error) {
    return {
      ok: false,
      permissionState: state,
      message: getGeoErrorMessage(error, t),
    };
  }
}

export default function TrackerInviteStart() {
  const { t } = useTranslation();
  const initialInviteParams = useMemo(() => getInviteParams(), []);

  const [acceptError, setAcceptError] = useState("");
  const [status, setStatus] = useState("ready");
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [pairingCodeInput, setPairingCodeInput] = useState("");
  const [pairingSubmitting, setPairingSubmitting] = useState(false);
  const [pairingClaimError, setPairingClaimError] = useState("");
  const [inviteToken, setInviteToken] = useState(initialInviteParams.inviteToken || "");
  const [orgId, setOrgId] = useState(initialInviteParams.orgId || "");
  const [androidBridgeAvailable, setAndroidBridgeAvailable] = useState(
    () => typeof window !== "undefined" && !!window.Android,
  );

  useEffect(() => {
    setAndroidBridgeAvailable(typeof window !== "undefined" && !!window.Android);
  }, []);

  useEffect(() => {
    const latest = getInviteParams();

    if (latest.inviteToken && latest.inviteToken !== inviteToken) {
      setInviteToken(latest.inviteToken);
    }

    if (latest.orgId && latest.orgId !== orgId) {
      setOrgId(latest.orgId);
    }

    console.log("[tracker-invite] params", {
      inviteToken: latest.inviteToken || inviteToken || null,
      orgId: latest.orgId || orgId || null,
    });
  }, [inviteToken, orgId]);

  useEffect(() => {
    if (!inviteToken) {
      setStatus("ready");
      setAcceptError("");
      return;
    }

    setStorageItem("inviteToken", inviteToken);
    console.log("[tracker-invite] inviteToken saved", { hasInviteToken: !!inviteToken });

    if (orgId) {
      setStorageItem("tracker_org_id", orgId);
      setStorageItem("org_id", orgId);
      setStorageItem("currentOrgId", orgId);
      console.log("[tracker-invite] orgId saved", orgId);
    }
  }, [inviteToken, orgId, t]);

  const authToken = inviteToken || null;

  const resolvedOrgId =
    orgId ||
    getStorageItem("tracker_org_id") ||
    getStorageItem("org_id") ||
    getStorageItem("currentOrgId") ||
    (typeof window !== "undefined" ? window.orgId || null : null);

  const isAndroid = useMemo(
    () => /Android/i.test(String(navigator.userAgent || "")),
    [],
  );

  const isInAppBrowser = useMemo(() => {
    const ua = String(navigator.userAgent || "").toLowerCase();
    return (
      ua.includes("wv") ||
      ua.includes("gmail") ||
      ua.includes("gsa") ||
      ua.includes("fbav") ||
      ua.includes("instagram") ||
      ua.includes("line") ||
      ua.includes("whatsapp")
    );
  }, []);

  async function persistTrackerSessionFromResponse(data) {
    const runtimeToken =
      data?.tracker_runtime_token ||
      data?.runtimeToken ||
      data?.runtime_token ||
      data?.session?.tracker_runtime_token ||
      data?.session?.runtimeToken ||
      null;

    const resolvedTrackerUserId =
      data?.tracker_user_id ||
      data?.trackerUserId ||
      data?.user_id ||
      data?.session?.tracker_user_id ||
      data?.session?.trackerUserId ||
      null;

    const persistedOrgId =
      data?.org_id ||
      data?.orgId ||
      data?.session?.org_id ||
      data?.session?.orgId ||
      resolvedOrgId ||
      null;

    const inviteId = data?.invite_id || data?.inviteId || data?.session?.invite_id || null;

    if (!runtimeToken || !resolvedTrackerUserId || !persistedOrgId) {
      console.error("[tracker-invite] accept response missing runtime fields", {
        hasRuntimeToken: !!runtimeToken,
        hasTrackerUserId: !!resolvedTrackerUserId,
        hasOrgId: !!persistedOrgId,
        data,
      });
      throw new Error("accept_response_missing_runtime_fields");
    }

    clearLegacyTrackerTokens();

    setStorageItem("tracker_runtime_token", runtimeToken);
    setStorageItem("tracker_access_token", runtimeToken);
    setStorageItem("access_token", runtimeToken);

    setStorageItem("tracker_user_id", resolvedTrackerUserId);
    setStorageItem("user_id", resolvedTrackerUserId);

    setStorageItem("tracker_org_id", persistedOrgId);
    setStorageItem("org_id", persistedOrgId);
    setStorageItem("currentOrgId", persistedOrgId);

    if (inviteId) {
      setStorageItem("tracker_invite_id", inviteId);
    }

    if (typeof window !== "undefined") {
      window.runtimeInviteToken = runtimeToken;
      window.trackerUserId = resolvedTrackerUserId;
      window.orgId = persistedOrgId;
    }

    console.log("[TRACKER_RUNTIME_PERSISTED]", {
      hasRuntimeToken: !!runtimeToken,
      hasTrackerUserId: !!resolvedTrackerUserId,
      hasOrgId: !!persistedOrgId,
      inviteId: inviteId || null,
    });

    return { runtimeToken, trackerUserId: resolvedTrackerUserId, orgId: persistedOrgId };
  }

  async function acceptInvite() {
    if (!authToken) {
      setStatus("missing_invite_token");
      setAcceptError(t("tracker.invite.errors.missingToken"));
      return null;
    }

    console.log("[tracker-invite] accepting invite", {
      hasInviteToken: !!authToken,
      hasOrgId: !!resolvedOrgId,
    });

    const response = await fetch("/api/accept-tracker-invite", {
      method: "POST",
      headers: {
        Authorization: `Bearer ${authToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        inviteToken: authToken,
        token: authToken,
        org_id: resolvedOrgId || null,
      }),
    });

    const data = await response.json().catch(() => ({}));

    if (!response.ok || !data?.ok) {
      throw new Error(
        data?.message ||
          data?.code ||
          data?.error ||
          "accept_tracker_invite_failed",
      );
    }

    const session = await persistTrackerSessionFromResponse(data);
    setStatus(data?.idempotent ? "already_accepted" : "accepted");
    return session;
  }

  async function claimPairingCodeAndContinue(e) {
    e?.preventDefault?.();
    e?.stopPropagation?.();

    const pairingCode = pairingCodeInput.trim();

    if (!pairingCode) {
      setPairingClaimError("Ingresa el codigo de emparejamiento.");
      return null;
    }

    const accessToken = readSupabaseAccessToken();

    if (!accessToken) {
      setPairingClaimError("Primero inicia sesion con Magic Link y luego ingresa el codigo.");
      return null;
    }

    try {
      setPairingSubmitting(true);
      setPairingClaimError("");
      setAcceptError("");
      setStatus("claiming_pairing_code");

      const response = await fetch("/api/accept-tracker-invite", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + accessToken,
        },
        body: JSON.stringify({
          action: "claim_pairing_code",
          pairing_code: pairingCode,
        }),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok || !data?.ok) {
        throw new Error(data?.message || data?.code || data?.error || "claim_pairing_code_failed");
      }

      const session = await persistTrackerSessionFromResponse(data);
      setStatus("accepted");

      const bridgeStarted = startNativeBridge(session);
      if (bridgeStarted) {
        setStatus("native_bridge_started");
      }

      redirectToTrackerGps(session);
      return session;
    } catch (error) {
      console.error("[tracker-invite] claim pairing code failed", error);
      setStatus("claim_pairing_failed");
      setPairingClaimError(error?.message || "claim_pairing_code_failed");
      return null;
    } finally {
      setPairingSubmitting(false);
    }
  }
  function startNativeBridge(session) {
    if (typeof window === "undefined" || !window.Android) return false;

    const runtimeToken = session?.runtimeToken;
    const trackerUserId = session?.trackerUserId;
    const persistedOrgId = session?.orgId;

    console.log("[tracker-invite] Android bridge available", {
      hasRuntimeToken: !!runtimeToken,
      trackerUserId,
      orgId: persistedOrgId,
    });

    try {
      if (window.Android.saveTrackerSession) {
        window.Android.saveTrackerSession(runtimeToken, trackerUserId, persistedOrgId);
      } else if (window.Android.startTracking) {
        window.Android.startTracking(runtimeToken, trackerUserId, persistedOrgId);
      }

      if (window.Android.requestStartTracking) {
        window.Android.requestStartTracking();
      }

      return true;
    } catch (error) {
      console.warn("[tracker-invite] Android bridge failed; continuing in web GPS page", error);
      return false;
    }
  }

  function redirectToTrackerGps(session) {
    const url = buildTrackerGpsUrl(session);

    setStorageItem("currentOrgId", session.orgId);
    setStorageItem("trackerAcceptedOrgId", session.orgId);
    setStorageItem("trackerAcceptedRedirect", url);

    console.log("[tracker-invite] redirecting to tracker gps", url);
    window.location.replace(url);
  }

  async function acceptInviteAndContinue() {
    try {
      setSubmitting(true);
      setAcceptError("");
      setStatus("accepting");

      const session = await acceptInvite();
      if (!session) return null;

      // Save to the Android bridge when available, but never stop in an intermediate
      // install/open-app screen. The authoritative next step is always TrackerGpsPage.
      const bridgeStarted = startNativeBridge(session);
      if (bridgeStarted) {
        setStatus("native_bridge_started");
      }

      redirectToTrackerGps(session);
      return session;
    } catch (error) {
      console.error("[tracker-invite] accept failed", error);
      setStatus("accept_failed");
      setAcceptError(error?.message || "accept_tracker_invite_failed");
      return null;
    } finally {
      setSubmitting(false);
    }
  }

  async function startPermissionStep(e) {
    e?.preventDefault?.();
    e?.stopPropagation?.();

    if (!consent) {
      setStatus("consent_required");
      setAcceptError(t("tracker.invite.errors.consentRequired"));
      return;
    }

    if (!authToken) {
      setStatus("missing_invite_token");
      setAcceptError(t("tracker.invite.errors.missingToken"));
      return;
    }

    // Android/TWA tracking permissions are handled by TrackerGpsPage. Do not stop
    // on the old “Ya tengo la app” intermediate activation screen.
    if (isAndroid || androidBridgeAvailable) {
      await acceptInviteAndContinue();
      return;
    }

    try {
      setAcceptError("");
      setStatus("checking_geo_permission");

      const permissionState = await getPermissionState();

      if (permissionState === "granted") {
        await acceptInviteAndContinue();
        return;
      }

      if (permissionState === "denied") {
        setStatus("geo_permission_required");
        setAcceptError(t("tracker.invite.errors.blockedSite"));
        return;
      }

      setStatus("geo_permission_step");
    } catch (error) {
      console.error("[tracker-invite] permission step failed", error);
      setStatus("geo_permission_step");
    }
  }

  async function handleGrantLocation() {
    try {
      setSubmitting(true);
      setAcceptError("");
      setStatus("requesting_geo_permission");

      const geo = await ensureGeolocationPermissionByPrompt(t);

      if (!geo.ok) {
        setStatus("geo_permission_required");
        setAcceptError(geo.message);
        return;
      }

      await acceptInviteAndContinue();
    } catch (error) {
      console.error("[tracker-invite] handleGrantLocation failed", error);
      setStatus("geo_permission_required");
      setAcceptError(t("tracker.invite.errors.locationUnavailableShort"));
    } finally {
      setSubmitting(false);
    }
  }

  async function retryGeolocation() {
    await handleGrantLocation();
  }

  async function handleAlreadyHasAppClick() {
    console.log("[tracker-invite] already-has-app clicked");
    await acceptInviteAndContinue();
  }

  const showPermissionCard =
    status === "geo_permission_step" ||
    status === "requesting_geo_permission";

  const showBlockedCard = status === "geo_permission_required";

  const primaryLabel = submitting
    ? t("tracker.invite.processing")
    : isAndroid || androidBridgeAvailable
      ? t("tracker.invite.openAppButton", "Ya tengo la app")
      : t("tracker.invite.acceptContinue");

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-slate-100">
      <div className="w-full max-w-md bg-white p-6 rounded-2xl shadow-xl">
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
          {t("tracker.invite.title")}
        </h1>

        <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700 leading-6">
          <p>{t("tracker.invite.intro")}</p>
        </div>

        <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 p-4">
          <p className="font-semibold text-emerald-900">Codigo de emparejamiento</p>
          <p className="mt-1 text-sm text-emerald-800">
            Si ya iniciaste sesion con Magic Link, ingresa aqui el codigo que te dio el administrador.
          </p>

          <input
            className="mt-3 w-full rounded-lg border border-emerald-200 bg-white px-3 py-2 text-center text-lg font-semibold tracking-widest text-slate-900 outline-none focus:ring"
            value={pairingCodeInput}
            onChange={(e) => {
              setPairingCodeInput(e.target.value.toUpperCase());
              setPairingClaimError("");
            }}
            placeholder="ABCD-1234-EFGH"
            autoCapitalize="characters"
            autoComplete="one-time-code"
          />

          <button
            type="button"
            onClick={claimPairingCodeAndContinue}
            disabled={pairingSubmitting || !pairingCodeInput.trim()}
            className="mt-3 w-full rounded-xl bg-emerald-700 py-3 font-medium text-white disabled:opacity-60"
          >
            {pairingSubmitting ? "Validando codigo..." : "Activar tracking con codigo"}
          </button>

          {pairingClaimError ? (
            <p className="mt-2 text-sm text-red-700">{pairingClaimError}</p>
          ) : null}
        </div>
        <label className="mt-4 flex items-start gap-3 text-sm text-slate-700">
          <input
            type="checkbox"
            checked={consent}
            onChange={(e) => {
              setConsent(e.target.checked);
              if (e.target.checked) {
                setAcceptError("");
                if (status === "consent_required" || status === "accept_failed") {
                  setStatus("ready");
                }
              }
            }}
            className="mt-1"
          />
          <span>{t("tracker.invite.consentLabel")}</span>
        </label>

        {!showPermissionCard && !showBlockedCard && (
          <button
            type="button"
            onClick={isAndroid || androidBridgeAvailable ? handleAlreadyHasAppClick : startPermissionStep}
            disabled={submitting || !authToken}
            className="w-full mt-5 rounded-xl bg-blue-600 text-white py-3 font-medium disabled:opacity-60"
          >
            {primaryLabel}
          </button>
        )}

        {showPermissionCard && (
          <div className="mt-4 rounded-xl border border-blue-200 bg-blue-50 p-4">
            <p className="text-blue-800 font-semibold">
              {t("tracker.invite.permissionCardTitle")}
            </p>

            <p className="mt-2 text-sm text-blue-700">
              {t("tracker.invite.permissionCardBody.before")}
              <strong> {t("tracker.invite.introStep.allow")}</strong>
              {t("tracker.invite.permissionCardBody.after")}
            </p>

            <button
              type="button"
              onClick={handleGrantLocation}
              disabled={submitting}
              className="w-full mt-3 rounded-lg bg-black text-white py-3 font-medium disabled:opacity-60"
            >
              {submitting && status === "requesting_geo_permission"
                ? t("tracker.invite.requestingPermission")
                : t("tracker.invite.permissionAction")}
            </button>

            <p className="mt-3 text-xs text-blue-700">
              {t("tracker.invite.permissionAutoContinue")}
            </p>
          </div>
        )}

        {showBlockedCard && (
          <div className="mt-4 rounded-xl border border-red-200 bg-red-50 p-4">
            <p className="text-red-700 font-semibold">
              {t("tracker.invite.blockedTitle")}
            </p>

            <p className="mt-2 text-sm text-red-600">
              {t("tracker.invite.blockedBody")}
            </p>

            {acceptError ? (
              <p className="mt-2 text-sm text-red-600">{acceptError}</p>
            ) : null}

            <button
              type="button"
              onClick={retryGeolocation}
              className="w-full mt-3 rounded-lg bg-black text-white py-3 font-medium"
            >
              {t("common.actions.retry")}
            </button>

            {isAndroid ? (
              <button
                type="button"
                onClick={handleAlreadyHasAppClick}
                disabled={submitting || !authToken}
                className="w-full mt-2 rounded-lg border border-slate-300 bg-white py-3 font-medium text-slate-800 disabled:opacity-60"
              >
                {t("tracker.invite.openAppButton", "Ya tengo la app")}
              </button>
            ) : null}

            {isInAppBrowser ? (
              <p className="mt-3 text-xs text-red-500">
                {t("tracker.invite.inAppBrowserHint.before")} <strong>{t("common.actions.openApp")}</strong>.
              </p>
            ) : null}

            <div className="mt-3 rounded-lg border border-red-100 bg-white/70 p-3 text-xs text-red-700">
              {t("tracker.invite.chromePath")}
            </div>
          </div>
        )}

        {!showPermissionCard && !showBlockedCard && acceptError ? (
          <div className="mt-3 text-sm text-red-600">{acceptError}</div>
        ) : null}

        <p className="mt-4 text-xs text-slate-500">
          {t("tracker.invite.statusLabel")}: {status}
        </p>
      </div>
    </div>
  );
}
